// A panel floating next to the element that opened it (menus, pickers).
//
// Promise:
// - Placed below the anchor (or above when there is no room), aligned to its
//   start, and kept inside the viewport. Position is fixed, so it never moves
//   the page and never needs a portal (the theme tokens are global anyway).
// - Escape and a press outside close it. Closing hands the focus back to the
//   anchor, so focus never jumps somewhere unexpected.
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface Placement {
  top: number;
  left: number;
  maxHeight: number;
}

const GAP = 4;
const MARGIN = 8;

export function placePopover(
  anchor: DOMRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  align: "start" | "end" = "start",
): Placement {
  const below = viewport.height - anchor.bottom - GAP - MARGIN;
  const above = anchor.top - GAP - MARGIN;
  const downward = panel.height <= below || below >= above;
  const room = downward ? below : above;
  const height = Math.min(panel.height, room);
  const top = downward ? anchor.bottom + GAP : anchor.top - GAP - height;
  const wanted = align === "start" ? anchor.left : anchor.right - panel.width;
  const left = Math.max(
    MARGIN,
    Math.min(wanted, viewport.width - panel.width - MARGIN),
  );
  return { top, left, maxHeight: Math.max(room, 80) };
}

export function Popover({
  anchor,
  open,
  onClose,
  align = "start",
  label,
  className = "",
  matchWidth = false,
  maxHeight,
  children,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose(): void;
  align?: "start" | "end";
  label?: string;
  className?: string;
  /** At least as wide as the anchor (a list of values under its field). */
  matchWidth?: boolean;
  /** Scrolls past this height even when there is more room. */
  maxHeight?: number;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<
    (Placement & { minWidth?: number }) | null
  >(null);

  // frontend-development: not a fetch; measures the anchor to place the panel.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const a = anchor.current?.getBoundingClientRect();
    const p = panel.current;
    if (!a || !p) return;
    const minWidth = matchWidth ? a.width : undefined;
    const placed = placePopover(
      a,
      {
        width: Math.max(p.offsetWidth, minWidth ?? 0),
        height: Math.min(p.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY),
      },
      { width: window.innerWidth, height: window.innerHeight },
      align,
    );
    setPlace({
      ...placed,
      maxHeight: Math.min(
        placed.maxHeight,
        maxHeight ?? Number.POSITIVE_INFINITY,
      ),
      minWidth,
    });
  }, [open, anchor, align, matchWidth, maxHeight]);

  // frontend-development: not a fetch; closes on Escape and outside presses.
  useEffect(() => {
    if (!open) return;
    const close = () => {
      anchor.current?.focus();
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;
  return (
    <div
      ref={panel}
      data-popover=""
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={`fixed z-50 min-w-[180px] max-w-[calc(100vw-16px)] overflow-y-auto [scrollbar-gutter:auto] rounded-[9px] border border-border bg-surface py-1 shadow-[var(--shadow-popover)] ${className}`}
      style={
        place
          ? {
              top: place.top,
              left: place.left,
              maxHeight: place.maxHeight,
              minWidth: place.minWidth,
            }
          : // Not `visibility: hidden`: a field inside that takes the focus
            // on mount (autoFocus) could not be focused while hidden.
            { top: 0, left: 0, opacity: 0 }
      }
    >
      {children}
    </div>
  );
}
